import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AurTopNav from '../common/aur-top-nav/AurTopNav';
import AurLeftNav, { LeftNavItem } from '../common/aur-left-nav/AurLeftNav';
import AurFooter from '../common/aur-footer/AurFooter';
import AurIcon, { IconColors, IconNames } from '../common/aur-icon/AurIcon';
import AurAskLio from '../aur-ask-lio/AurAskLio';
import AurUserProfile from '../user-profile/AurUserProfile';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { Logout } from '@mui/icons-material';
import { setIsChatPanelVisible } from '../../redux/slices/askLioSlice';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthorizationContext } from '../../contexts/authorizationContext/AuthorizationContext';
import { Permissions, Resources } from '../../utils/authorization';
import AurClientDropDownWrapper from './AurClientDropDownWrapper';
import { ReactComponent as Glow } from '../../assests/svg/glow.svg';

import {
  selectUser,
  selectUserAuthzLoadedAt,
} from '../../redux/slices/userSlice';
import { t } from 'i18next';
import {
  resetTrigger,
  setShowModal,
  setShouldTriggerAction,
} from '../../redux/slices/unSaveChangesModalSlice';
import { LogoutPopup } from '../logout-popup/LogoutPopup';
import { logoutAction } from '../../redux/rootAction';
import { logout as logoutService } from '../../api/Authentication';
import navConstants from '../../constants/navConstants';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../constants/analyticsConstants';
import {
  resetAnalyticUserState,
  setReportSrc,
} from '../../redux/slices/analyticsSlice';
import { setNavigate } from '../../api';
import { resetQualtricsData } from '../../utils/qualtricsUtils';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../redux/slices/featureSettingsSlice';

const CLIENT_DROPDOWN_PATHS = new Set([
  '/reports',
  '/welcome-single-client',
  '/eci-home-page/manage-overlays',
  '/index-search',
  '/nlp-search',
  '/users/exportdetails',
  '/reports/invoices',
  '/business-reports',
]);

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const i = useContext(AuthorizationContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackGAEvent } = useAnalytics();
  const { showIndexBanner } = useSelector(
    (state: RootState) => state.indexesBanner.indexBannerData
  );
  const user = useSelector(selectUser);
  const authzLoadedAt = useSelector(selectUserAuthzLoadedAt);
  const [leftNavItems, setLeftNavItems] = useState<LeftNavItem[]>([]);
  const [lioVisible, setLioVisible] = useState<boolean>(false);
  const [leftNavVisible, setLeftNavVisible] = useState<boolean>(false);
  const [logoutModalVisibe, setLogoutModalVisibe] = useState<boolean>(false);
  const [logoutReqRunning, setLogoutReqRunning] = useState<boolean>(false);

  const hideClientDropdown =
    useSelector((state: RootState) => state.selectedClient)
      .hideClientDropdown || false;

  const isLioOpen = useSelector(
    (state: RootState) => state.askLioSlice.isChatPanelVisible
  );

  const triggerLogout = useSelector(
    (state: RootState) => state.unsavedChangesModal.triggerLogout
  );
  const shouldTriggerAction = useSelector(
    (state: RootState) => state.unsavedChangesModal.shouldTriggerAction
  );
  const isPowerBIReportsEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.PowerBIReportsEnabled)
  );

  if (!user) {
    // todo:chrise: this is only to solve for a nullability conflict. Resolve by updating further down scope.
    return <>BUGCHECK: No user but authenticated route.</>;
  }

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  const toggleLioChatPanel = () => {
    if (!isLioOpen) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('askLio.askLioAriaLabel'),
          region: GA_REGIONS.LEFT_RAIL,
          type: GA_CLICK_TYPES.ICON,
          action: t('gaTracking.global.leftNavClick', {
            name: t('askLio.askLioAriaLabel'),
          }),
        },
      });
    }
    dispatch(setIsChatPanelVisible(!isLioOpen));
  };

  // Determine if the current route matches
  const isCurrentRoute = (route: string) => {
    const routeToMatch = route.toLowerCase();
    const currentPath = location.pathname.toLowerCase();

    // Match the base route only. Additional routes or parameters are ignored.
    // The following examples all match '/users'
    // - /users?userId=1&orgId=2&message=welcome
    // - /users/1?orgId=2
    // - /users/1/edit
    return currentPath.startsWith(routeToMatch);
  };

  useEffect(() => {
    if (shouldTriggerAction) {
      performLogout();
      // Important: Reset after triggering
    }
  }, [triggerLogout, dispatch]);

  const trackGA = (item: LeftNavItem) => {
    if (item.name === 'conciergereports') {
      dispatch(setReportSrc(GA_REGIONS.LEFT_RAIL));
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: item.tooltip || item.name,
        action: t('gaTracking.global.leftNavClick', {
          name: item.tooltip || item.name,
        }),
        region: GA_REGIONS.LEFT_RAIL,
        type: GA_CLICK_TYPES.ICON,
      },
    });
  };

  const prepareNavItems = () => {
    const items: LeftNavItem[] = [];
    items.push({
      name: 'welcome',
      icon: <AurIcon icon={IconNames.Home} />,
      route: '/welcome-multi-client',
      // for making route active which are not same as current route
      activeRoutes: ['/welcome-single-client'],
      tooltip: t('clientHomePage.welcomePage'),
      onClick: trackGA,
    });
    if (i.can(Permissions.View, Resources.Clients)) {
      items.push({
        name: 'clients',
        icon: <AurIcon icon={IconNames.Clients} />,
        route: '/clients',
        tooltip: t('clientHomePage.manageClients'),
        onClick: trackGA,
      });
    }
    if (i.can(Permissions.View, Resources.Users)) {
      items.push({
        name: 'users',
        icon: <AurIcon icon={IconNames.Users} />,
        route: '/users',
        tooltip: t('clientHomePage.manageUsers'),
        onClick: trackGA,
      });
    }
    if (isPowerBIReportsEnabled) {
      items.push({
        name: 'Business-Reports',
        icon: (
          <AurIcon
            icon={IconNames.BarChart}
            color={IconColors.Custom}
            customClassName="union-icon-custom-class"
          />
        ),
        route: '/business-reports',
        tooltip: t('powerBiReports.title'),
        onClick: trackGA,
      });
    }


    if (i.can(Permissions.View, Resources.Reports_ConciergeUpload)) {
      items.push({
        name: 'conciergereports',
        icon: (
          <AurIcon
            icon={IconNames.Union}
            color={IconColors.Custom}
            customClassName="union-icon-custom-class"
          />
        ),
        route: '/customreports',
        tooltip: t('conciergeReports.toolTips.navToolTip'),
        onClick: trackGA,
      });
    }

    const mutatedItems = items.map((item) => ({
      ...item,
      active: isCurrentRoute(item.route),
    }));

    setLeftNavItems(mutatedItems);
  };

  const prepareLioVisible = () => {
    const visible = i.can(Permissions.View, Resources.Lio);
    setLioVisible(visible);
  };

  const prepareLeftNavVisible = () => {
    if (i.rules.length > 0) setLeftNavVisible(true);
  };

  // The current configuration of perms/items here needs work.
  // Most importantly this solves the issue that authz dissapears after reload.
  // Below is a temporary and quick measure, and /needs/ to be reworked futher.
  useEffect(() => {
    prepareLeftNavVisible();
    prepareNavItems();
    prepareLioVisible();
  }, [authzLoadedAt]); //will re-calculate at every change of state for authz loaded at.

  const handleLogout = () => {
    // Prevent click when logout request already in progress. Can't disabled button due to required active button ui state
    if (logoutReqRunning) return;
    localStorage.setItem('currentLink', '/logout');
    const path = localStorage.getItem('currentPath');
    if (
      path === '/users/bulkupload/results' ||
      path === '/users/add' ||
      path === '/index-search' ||
      path === '/nlp-search' ||
      path === '/upload-file-catalyst'
    ) {
      dispatch(setShouldTriggerAction());
      closeLogoutModal();
      dispatch(setShowModal(true));
      return;
    }
    sessionStorage.removeItem('viewedClientIds');
    performLogout();
  };

  // commenting to resolve lint error

  //  const handleFeedback = useCallback(() => {
  //    trackGAEvent({
  //     eventName: GA_EVENT_NAMES.CLICK,
  //     click: {
  //       name: t('clientHomePage.feedback'),
  //       region: GA_REGIONS.TOP_RAIL,
  //       type: GA_CLICK_TYPES.LINK,
  //       action: t('gaTracking.global.topNavClick', {
  //         name: t('clientHomePage.feedback'),
  //       }),
  //     },
  //   });
  //   localStorage.setItem('currentLink', '/feedback');
  //   const path = localStorage.getItem('currentPath');
  //   if (
  //     path === '/users/bulkupload/results' ||
  //     path === '/users/add' ||
  //     path === '/index-search' ||
  //     path === '/nlp-search' ||
  //     path === '/upload-file-catalyst'
  //   ) {
  //     dispatch(setShowModal(true));
  //   } else {
  //     navigate('/feedback');
  //   }

  // }, []);

  const performLogout = async () => {
    setLogoutReqRunning(true);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.logout'),
        region: GA_REGIONS.TOP_RAIL,
        type: GA_CLICK_TYPES.DROPDOWN,
        action: t('gaTracking.global.topNavClick', {
          name: t('gaTracking.global.logout'),
        }),
      },
    });
    try {
      await logoutService(dispatch);
    } finally {
      setLogoutReqRunning(false);
      closeLogoutModal();
      navigate(navConstants.auth.login, {
        replace: true,
      });
      dispatch(resetTrigger());
      dispatch(logoutAction());
      dispatch(resetAnalyticUserState());
      resetQualtricsData();
    }
  };

  const closeLogoutModal = () => {
    setLogoutModalVisibe(false);
  };

  const openLogoutModal = () => {
    setLogoutModalVisibe(true);
  };

  const menuItems = useMemo(
    () => [
      {
        label: (
          <Typography className="typo-text body2_size">
            {t('common.logout')}
          </Typography>
        ),
        icon: <Logout fontSize="small" />,
        onClick: openLogoutModal,
      },
    ],
    []
  );

  return (
    <Box
      className={`${isLioOpen ? 'aur-content-wrapper-margin' : 'aur-content-wrapper-no-margin'}`}
    >
      <LogoutPopup
        closeModal={() => {
          setLogoutModalVisibe(false);
        }}
        modalIsOpen={logoutModalVisibe}
        logOut={handleLogout}
        showLoading={logoutReqRunning}
      />
      <div className="aur-layout">
        <Glow style={{ position: 'fixed' }} aria-hidden="true" />
        <div>
          {CLIENT_DROPDOWN_PATHS.has(location.pathname) &&
            !hideClientDropdown && (
              <AurClientDropDownWrapper
                routeName={location.pathname}
                sortColumn="Name"
              />
            )}
        </div>
        <header className="aur-layout-top">
          <AurTopNav>
            <Box className="d-flex">
              <h1 className="sr-only" id="welcome-msg">
                {t('app.welcomeMessage')}
              </h1>
              {/* <AurButton
                dataTestId="feedback-link"
                type="text"
                size="Small"
                label={t('clientHomePage.feedback')}
                startIcon={<Feedback aria-hidden="true" />}
                onClick={handleFeedback}
                ariaDescribedBy="welcome-msg"
              /> */}

              <AurUserProfile user={user} menuItems={menuItems} />
            </Box>
          </AurTopNav>
        </header>
        {leftNavVisible ? (
          <>
            <nav
              data-testid="aur-left-nav-container"
              className={
                showIndexBanner ? 'aur-layout-left-index' : 'aur-layout-left'
              }
            >
              <AurLeftNav
                navItems={leftNavItems}
                isLioVisible={lioVisible}
                isLioOpen={isLioOpen}
                setIsLioOpen={toggleLioChatPanel}
              />
            </nav>
          </>
        ) : (
          <></>
        )}

        <div
          data-testid="no-left-nav"
          className={
            leftNavVisible ? 'aur-layout-body' : 'aur-layout-body no-left-nav'
          }
        >
          <main className="aur-layout-content">{children}</main>
          <footer className="aur-layout-footer">
            <AurFooter />
          </footer>
        </div>

        <AurAskLio
          testId="aurAskLioPanel"
          isLioOpen={isLioOpen}
          onClose={toggleLioChatPanel}
        />
      </div>
    </Box>
  );
};

export default DashboardLayout;
